import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Download, ShieldCheck, Lock, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function quartersBack(n = 8) {
  const out = [];
  const today = new Date();
  let year = today.getFullYear();
  let q = Math.floor(today.getMonth() / 3) + 1;
  for (let i = 0; i < n; i++) {
    out.push(`${year}-Q${q}`);
    q -= 1;
    if (q < 1) {
      q = 4;
      year -= 1;
    }
  }
  return out;
}

const ACCESS_REVIEW_WINDOWS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "60", label: "Last 60 days" },
  { value: "90", label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
  { value: "365", label: "Last 365 days" },
];

export default function Compliance() {
  const { user } = useAuth();
  const choices = quartersBack(12);
  const [quarter, setQuarter] = useState(choices[0]);
  const [busy, setBusy] = useState(false);
  // Phase 9 — access review
  const [reviewDays, setReviewDays] = useState("90");
  const [reviewBusy, setReviewBusy] = useState(false);
  // Phase 10 — PHI summary tile
  const [phiSummary, setPhiSummary] = useState(null);

  const allowed = user?.role === "compliance" || user?.role === "legal_review";

  useEffect(() => {
    if (!allowed) return;
    api
      .get("/compliance/phi-summary?days=30")
      .then((r) => setPhiSummary(r.data))
      .catch(() => setPhiSummary(null));
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-12 text-center text-[color:var(--page-fg-muted)]">
        Compliance or Legal Review role required.
      </div>
    );
  }

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/compliance/attestation.pdf?quarter=${quarter}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `goodwin-er-attestation-${quarter}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Attestation ${quarter} downloaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadAccessReview = async () => {
    setReviewBusy(true);
    try {
      const res = await api.get(`/compliance/access-review.pdf?days=${reviewDays}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `goodwin-access-review-${reviewDays}d.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Access review (${reviewDays} days) downloaded`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <div data-testid="compliance-page" className="mx-auto max-w-4xl px-6 py-8 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--page-fg)]">Compliance</h1>
        <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
          Quarterly attestation reports and HIPAA access reviews for the audit committee.
        </p>
      </div>

      {/* Phase 10 — PHI activity tile */}
      {phiSummary && (
        <div
          data-testid="phi-summary-tile"
          className="brand-card p-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(20,184,184,0.06), rgba(245,158,11,0.06))",
          }}
        >
          <div className="flex items-start gap-3">
            <span
              className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
              style={{ backgroundColor: "rgba(229,77,77,0.14)", color: "#C03030" }}
            >
              <ShieldAlert className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-[color:var(--page-fg)]">
                PHI activity — last {phiSummary.days} days
              </h3>
              <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div data-testid="phi-stat-flagged">
                  <p className="text-[10px] uppercase tracking-wide text-[color:var(--page-fg-muted)]">
                    Flagged
                  </p>
                  <p className="text-2xl font-semibold text-[color:var(--page-fg)]">
                    {phiSummary.flagged_in_period}
                  </p>
                </div>
                <div data-testid="phi-stat-overrides">
                  <p className="text-[10px] uppercase tracking-wide text-[color:var(--page-fg-muted)]">
                    Overrides
                  </p>
                  <p className="text-2xl font-semibold text-[color:var(--page-fg)]">
                    {phiSummary.overrides_in_period}
                  </p>
                </div>
                <div data-testid="phi-stat-pending-llm">
                  <p className="text-[10px] uppercase tracking-wide text-[color:var(--page-fg-muted)]">
                    Pending LLM scan
                  </p>
                  <p className="text-2xl font-semibold text-[color:var(--page-fg)]">
                    {phiSummary.pending_llm_scan}
                  </p>
                </div>
                <div data-testid="phi-stat-encrypted">
                  <p className="text-[10px] uppercase tracking-wide text-[color:var(--page-fg-muted)]">
                    Encrypted at rest
                  </p>
                  <p className="text-2xl font-semibold text-[color:var(--page-fg)]">
                    {phiSummary.encrypted_total}
                    <span className="text-sm font-normal text-[color:var(--page-fg-muted)]">
                      {" "}/ {phiSummary.documents_total}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="brand-card p-6" data-testid="attestation-card">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(20, 184, 184, 0.16)", color: "#5EE0E0" }}
          >
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
              Quarterly attestation PDF
            </h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              Audit-log integrity, MFA coverage, lockout/unlock events, SSO sign-ins, backup
              health, notification delivery, high-risk case throughput, storage health.
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-[color:var(--page-fg-muted)]">Quarter</Label>
                <Select value={quarter} onValueChange={setQuarter}>
                  <SelectTrigger
                    data-testid="attestation-quarter-select"
                    className="w-[180px]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {choices.map((q) => (
                      <SelectItem key={q} value={q}>
                        {q}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={download}
                disabled={busy}
                data-testid="attestation-download-button"
                className="bg-brand-teal text-white hover:bg-brand-tealDark"
              >
                <Download className="mr-1 h-4 w-4" />
                {busy ? "Generating…" : "Download attestation"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="brand-card p-6" data-testid="access-review-card">
        <div className="flex items-start gap-3">
          <span
            className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl"
            style={{ backgroundColor: "rgba(245, 158, 11, 0.16)", color: "#E8CB8E" }}
          >
            <Lock className="h-4 w-4" />
          </span>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-[color:var(--page-fg)]">
              HIPAA access review PDF
            </h2>
            <p className="mt-1 text-xs text-[color:var(--page-fg-muted)]">
              Document access activity, denied attempts, PHI override events, restricted
              inventory, and encryption posture for the selected window. Generated for the
              periodic access-review per 45 CFR § 164.308(a)(1)(ii)(D).
            </p>
            <div className="mt-4 flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-[color:var(--page-fg-muted)]">Window</Label>
                <Select value={reviewDays} onValueChange={setReviewDays}>
                  <SelectTrigger
                    data-testid="access-review-window-select"
                    className="w-[180px]"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCESS_REVIEW_WINDOWS.map((w) => (
                      <SelectItem key={w.value} value={w.value}>
                        {w.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={downloadAccessReview}
                disabled={reviewBusy}
                data-testid="access-review-download-button"
                className="bg-brand-teal text-white hover:bg-brand-tealDark"
              >
                <Download className="mr-1 h-4 w-4" />
                {reviewBusy ? "Generating…" : "Download access review"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
